import { z } from "zod";
import { defineEvent } from "./registry.js";
/**
 * Market olayları (Prompt 16). Yük KİŞİSEL VERİ TAŞIMAZ; tutarlar kuruş dizgesi, miktarlar 3 ondalık dizgedir.
 * Web `^market\.` olaylarında stok, transfer, fire, sayım ve kapanış sorgularını tazeler.
 */
const ledgerStatus = z.enum(["NONE", "POSTED", "QUEUED"]);
export const marketTransferSentV1 = defineEvent({
    type: "market.transfer.sent",
    version: 1,
    description: "İstasyonlar arası transfer gönderildi (gönderen depodan stok çıktı; alan istasyonda kabul bekliyor)",
    realtime: ["station", "tenant"],
    payload: z.object({
        transferId: z.uuid(),
        companyId: z.uuid(),
        transferNo: z.string(),
        fromStationId: z.uuid(),
        fromWarehouseId: z.uuid(),
        toStationId: z.uuid(),
        toWarehouseId: z.uuid(),
        lineCount: z.number().int(),
        sentValue: z.string().describe("Kuruş"),
        sentAt: z.iso.datetime({ offset: true }),
    }),
});
export const marketTransferReceivedV1 = defineEvent({
    type: "market.transfer.received",
    version: 1,
    description: "Transfer alan istasyonda kabul edildi (stok girdi, eksik fire yazıldı, fiş kesildi)",
    realtime: ["station", "tenant"],
    payload: z.object({
        transferId: z.uuid(),
        companyId: z.uuid(),
        transferNo: z.string(),
        fromStationId: z.uuid(),
        toStationId: z.uuid(),
        sentValue: z.string(),
        receivedValue: z.string(),
        shortValue: z.string(),
        journalEntryId: z.uuid().nullable(),
        ledgerStatus,
        receivedAt: z.iso.datetime({ offset: true }),
    }),
});
export const marketTransferCancelledV1 = defineEvent({
    type: "market.transfer.cancelled",
    version: 1,
    description: "Taslak transfer iptal edildi (stok hareketi yoktu)",
    realtime: ["station"],
    payload: z.object({
        transferId: z.uuid(),
        companyId: z.uuid(),
        transferNo: z.string(),
        fromStationId: z.uuid(),
        toStationId: z.uuid(),
    }),
});
export const marketWriteOffPostedV1 = defineEvent({
    type: "market.write_off.posted",
    version: 1,
    description: "Fire / imha onaylandı: stoktan düştü ve kayıp fişi kesildi",
    realtime: ["station"],
    payload: z.object({
        writeOffId: z.uuid(),
        companyId: z.uuid(),
        stationId: z.uuid(),
        writeOffNo: z.string(),
        reason: z.string(),
        lossKey: z.string(),
        lineCount: z.number().int(),
        totalValue: z.string(),
        journalEntryId: z.uuid().nullable(),
        ledgerStatus,
    }),
});
export const marketWriteOffRejectedV1 = defineEvent({
    type: "market.write_off.rejected",
    version: 1,
    description: "Fire / imha talebi reddedildi (stok değişmedi)",
    realtime: ["station"],
    payload: z.object({
        writeOffId: z.uuid(),
        companyId: z.uuid(),
        stationId: z.uuid(),
        writeOffNo: z.string(),
        note: z.string().nullable(),
    }),
});
export const marketCountSubmittedV1 = defineEvent({
    type: "market.count.submitted",
    version: 1,
    description: "Sayım gönderildi: farklar hesaplandı, onay bekliyor",
    realtime: ["station"],
    payload: z.object({
        countId: z.uuid(),
        companyId: z.uuid(),
        stationId: z.uuid(),
        countNo: z.string(),
        kind: z.string(),
        lineCount: z.number().int(),
        shortageValue: z.string(),
        surplusValue: z.string(),
    }),
});
export const marketCountApprovedV1 = defineEvent({
    type: "market.count.approved",
    version: 1,
    description: "Sayım farkı onaylandı: stok düzeltildi ve sayım farkı fişi kesildi",
    realtime: ["station"],
    payload: z.object({
        countId: z.uuid(),
        companyId: z.uuid(),
        stationId: z.uuid(),
        countNo: z.string(),
        kind: z.string(),
        lineCount: z.number().int(),
        adjustedLineCount: z.number().int(),
        shortageValue: z.string(),
        surplusValue: z.string(),
        journalEntryId: z.uuid().nullable(),
        ledgerStatus,
    }),
});
export const marketDailyClosingClosedV1 = defineEvent({
    type: "market.daily_closing.closed",
    version: 1,
    description: "Market gün kapanışı yapıldı (SMM fişi)",
    realtime: ["station"],
    payload: z.object({
        closingId: z.uuid(),
        companyId: z.uuid(),
        stationId: z.uuid(),
        businessDate: z.iso.date(),
        cogsAmount: z.string(),
        movementCount: z.number().int(),
        lateMovementCount: z.number().int(),
        journalEntryId: z.uuid().nullable(),
        ledgerStatus,
    }),
});
export const marketStockNegativeV1 = defineEvent({
    type: "market.stock.negative",
    version: 1,
    description: "Bir ürünün depo bakiyesi negatife düştü (kasa satışı reddedilmez; sayım / mal kabul gerekir)",
    realtime: ["station"],
    payload: z.object({
        companyId: z.uuid(),
        stationId: z.uuid(),
        warehouseId: z.uuid(),
        productId: z.uuid(),
        quantity: z.string().describe("Hareketten sonraki miktar (negatif)"),
        movementType: z.string(),
        sourceDocType: z.string(),
        sourceDocId: z.uuid(),
    }),
});
export const marketOpeningStockRecordedV1 = defineEvent({
    type: "market.opening_stock.recorded",
    version: 1,
    description: "Depoya açılış stoğu girildi (muhasebe fişi yok: 153 açılışı muhasebe açılış fişindedir)",
    realtime: ["station"],
    payload: z.object({
        batchId: z.uuid(),
        companyId: z.uuid(),
        stationId: z.uuid(),
        warehouseId: z.uuid(),
        lineCount: z.number().int(),
        totalValue: z.string(),
    }),
});
// ── Aşama B: satın alma, eşleştirme, lisans ve SKT ──────────────────────────
export const marketPurchaseOrderStatusChangedV1 = defineEvent({
    type: "market.purchase_order.status_changed",
    version: 1,
    description: "Tedarikçi siparişinin durumu değişti (onaya gönderildi, onaylandı, reddedildi, gönderildi, kısmen / tamamen " +
        "teslim alındı, kapatıldı, iptal edildi)",
    realtime: ["station"],
    payload: z.object({
        orderId: z.uuid(),
        companyId: z.uuid(),
        stationId: z.uuid(),
        supplierId: z.uuid(),
        orderNo: z.string(),
        from: z.string(),
        to: z.string(),
        netAmount: z.string().describe("Kuruş, KDV hariç"),
    }),
});
export const marketGoodsReceiptPostedV1 = defineEvent({
    type: "market.goods_receipt.posted",
    version: 1,
    description: "Mal kabul kaydedildi: sağlam miktar geçici maliyetle stoğa girdi (fiş YOK — alış fişi faturadadır)",
    realtime: ["station"],
    payload: z.object({
        receiptId: z.uuid(),
        companyId: z.uuid(),
        stationId: z.uuid(),
        warehouseId: z.uuid(),
        supplierId: z.uuid(),
        purchaseOrderId: z.uuid().nullable(),
        receiptNo: z.string(),
        despatchNo: z.string().nullable(),
        lineCount: z.number().int(),
        receivedQuantity: z.string(),
        damagedQuantity: z.string(),
        netAmount: z.string().describe("Kuruş (geçici maliyetle)"),
    }),
});
export const marketGoodsReceiptCancelledV1 = defineEvent({
    type: "market.goods_receipt.cancelled",
    version: 1,
    description: "Mal kabul iptal edildi (kaydedilmişse stok ters hareketle çıktı)",
    realtime: ["station"],
    payload: z.object({
        receiptId: z.uuid(),
        companyId: z.uuid(),
        stationId: z.uuid(),
        receiptNo: z.string(),
        wasPosted: z.boolean(),
        reversedValue: z.string().describe("Kuruş (ters hareketlerin toplam değeri, pozitif)"),
    }),
});
export const marketInvoiceMatchCreatedV1 = defineEvent({
    type: "market.invoice_match.created",
    version: 1,
    description: "Alış faturası mal kabul(ler)le eşleştirildi (tam eşleşme ya da fark onayı bekliyor)",
    realtime: ["tenant"],
    payload: z.object({
        matchId: z.uuid(),
        companyId: z.uuid(),
        invoiceId: z.uuid(),
        supplierId: z.uuid(),
        status: z.string(),
        result: z.string(),
        receiptCount: z.number().int(),
        amountDifference: z.string(),
        automatic: z.boolean(),
    }),
});
export const marketInvoiceMatchCostFinalizedV1 = defineEvent({
    type: "market.invoice_match.cost_finalized",
    version: 1,
    description: "Eşleşmenin kesin maliyeti uygulandı: maliyet düzeltmesi hareketleri ve satılmış pay için SMM düzeltmesi",
    realtime: ["tenant"],
    payload: z.object({
        matchId: z.uuid(),
        companyId: z.uuid(),
        invoiceId: z.uuid(),
        costAdjustment: z.string().describe("Kuruş: toplam fark (stok + SMM payı)"),
        stockAdjustment: z.string().describe("Kuruş: eldeki stoğa giden pay"),
        cogsAdjustment: z.string().describe("Kuruş: sonraki SMM kapanışına giden (satılmış) pay"),
    }),
});
export const marketInvoiceMatchUnmatchedV1 = defineEvent({
    type: "market.invoice_match.unmatched",
    version: 1,
    description: "Fatura eşleşmesi geri alındı (kesin maliyet terslendi; mal kabuller yeniden eşleştirilebilir)",
    realtime: ["tenant"],
    payload: z.object({
        matchId: z.uuid(),
        companyId: z.uuid(),
        invoiceId: z.uuid(),
        previousStatus: z.string(),
        reversedAdjustment: z.string().describe("Kuruş: terslenen kesin maliyet farkı"),
        reason: z.string(),
    }),
});
/** ALARM: istasyon satış belgesinin (tütün / alkol) bitişine `licenseWarningDays` kaldı (Prompt 23 uyum takvimi). */
export const marketLicenseExpiringV1 = defineEvent({
    type: "market.license.expiring",
    version: 1,
    description: "İstasyon satış belgesinin (tütün / alkol) süresi dolmak üzere",
    realtime: ["station", "tenant"],
    payload: z.object({
        licenseId: z.uuid(),
        companyId: z.uuid(),
        stationId: z.uuid(),
        licenseType: z.string(),
        documentNo: z.string(),
        validUntil: z.iso.date(),
        daysLeft: z.number().int(),
    }),
});
/** ALARM: belgenin süresi doldu (v1'de satış ENGELLENMEZ — mevzuat sorusu #62). */
export const marketLicenseExpiredV1 = defineEvent({
    type: "market.license.expired",
    version: 1,
    description: "İstasyon satış belgesinin (tütün / alkol) süresi doldu",
    realtime: ["station", "tenant"],
    payload: z.object({
        licenseId: z.uuid(),
        companyId: z.uuid(),
        stationId: z.uuid(),
        licenseType: z.string(),
        documentNo: z.string(),
        validUntil: z.iso.date(),
    }),
});
const lotAlertPayload = z.object({
    lotId: z.uuid(),
    companyId: z.uuid(),
    stationId: z.uuid(),
    warehouseId: z.uuid(),
    productId: z.uuid(),
    lotNo: z.string().nullable(),
    expiryDate: z.iso.date(),
    daysToExpiry: z.number().int(),
    quantity: z.string(),
});
/** ALARM: partinin SKT'sine istasyonun uyarı günü kadar kaldı (parti başına bir kez). */
export const marketLotExpiringV1 = defineEvent({
    type: "market.lot.expiring",
    version: 1,
    description: "Stoktaki bir partinin son kullanma tarihi yaklaşıyor",
    realtime: ["station"],
    payload: lotAlertPayload,
});
/** ALARM: stokta SKT'si geçmiş parti var (parti başına bir kez) — fire / imha gerekir. */
export const marketLotExpiredV1 = defineEvent({
    type: "market.lot.expired",
    version: 1,
    description: "Stokta son kullanma tarihi geçmiş parti var",
    realtime: ["station"],
    payload: lotAlertPayload,
});
//# sourceMappingURL=market.v1.js.map